import type { ControlInput, GameSnapshot, MissionObjective, Role } from '../../shared/types.js';
import { buildMissionDetail, buildSkipMissionButton, isWaypointAtMission, missionStates, type MissionVisualState } from './missionDetail.js';
import { buildGroupViews, groupProgressLabel, isGroupRow, needsPanelRender, type MissionGroupView } from './missionGroups.js';
import type { LogEntry, LogKind } from '../../shared/narrative.js';
import { addLogToCatalog, countUnreadLogs, getFoundLogIds, getLogStoreKey, getRecoveredLogs, isLogRead, loadLogCatalog, markLogRead, onLogsChanged, replayLog } from '../narrativeLogs.js';
import { t } from '../i18n.js';
import { itemDisplayName, loadItemNames } from '../itemNames.js';
import { state as controllerState } from '../controllerState.js';
import { createCard, createReadoutRow, resourceReadoutVariant, type ReadoutVariant } from '../controllerUiHelpers.js';
import type { ControllerNetwork } from '../controllerNetwork.js';
import {
  FUEL_PRODUCT_ID_BY_TYPE,
  FUEL_PROFILE_BY_TYPE,
  FUEL_TYPES,
  SIMULATION_INTERVAL_MS
} from '../../shared/constants.js';
import {
  getBearingToMapPoint,
  headingDegreesToCardinal,
  normalizeHeadingDegrees,
  simulationToMapPoint,
  submarineToMapPoint
} from '../../shared/spatial.js';

type RoleMeta = { label: string; icon: string };

type CreateCaptainRoomControllerOptions = {
  controls: HTMLDivElement | null;
  ctrlZoneLeftBottom: HTMLDivElement | null;
  ctrlZoneRightAux: HTMLDivElement | null;
  emitControl: (input: ControlInput) => void;
  resolveRoleMeta: (role: Role) => RoleMeta;
  resolveActiveMap: () => import('../../shared/maps/types.js').MapDefinition | null;
  getControllerNetwork: () => ControllerNetwork | null;
  requestSaveGame: () => void;
};

type CaptainTabId = 'mission' | 'log' | 'submarine' | 'crew' | 'weapons' | 'status' | 'credits';

const CAPTAIN_TABS: Array<{ id: CaptainTabId; label: string }> = [
  { id: 'mission', label: 'Mission' },
  { id: 'log', label: 'Log' },
  { id: 'submarine', label: 'Submarine' },
  { id: 'crew', label: 'Crew' },
  { id: 'weapons', label: 'Weapons' },
  { id: 'status', label: 'Status' },
  { id: 'credits', label: 'Credits' }
];

const KICK_CONFIRM_TIMEOUT_MS = 4000;

const setSeg = (el: HTMLElement, text: string, variant: ReadoutVariant = 'amber'): void => {
  el.className = `ctrl-seg-display${variant === 'amber' ? '' : ` ${variant}`}`;
  el.textContent = text;
};

const formatClock = (totalMs: number): string => {
  const totalSec = Math.max(0, Math.floor(totalMs / 1000));
  const hours = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const mm = String(mins).padStart(2, '0');
  const ss = String(secs).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
};

const formatDistance = (meters: number): string =>
  meters >= 1000 ? `${(meters / 1000).toFixed(2)} km` : `${Math.round(meters)} m`;

export const createCaptainRoomController = ({
  controls,
  ctrlZoneLeftBottom,
  emitControl,
  resolveRoleMeta,
  getControllerNetwork,
  requestSaveGame
}: CreateCaptainRoomControllerOptions) => {
  let activeTab: CaptainTabId = 'mission';
  const tabButtons = new Map<CaptainTabId, HTMLButtonElement>();
  const tabPanels = new Map<CaptainTabId, HTMLDivElement>();

  // Mission tab
  let captainMissionList: HTMLUListElement | null = null;
  let captainMissionTimerEl: HTMLElement | null = null;

  // Log tab
  let logPanelBody: HTMLDivElement | null = null;
  let logSignature = '';
  /** The log body element the last render filled; a rebuilt (empty) body must render even when the signature is unchanged. */
  let renderedLogBody: HTMLDivElement | null = null;
  let selectedLogId: string | null = null;
  let logPulseTimer: number | null = null;

  // Submarine tab readouts
  let depthValue: HTMLElement | null = null;
  let speedValue: HTMLElement | null = null;
  let vRateValue: HTMLElement | null = null;
  let headingValue: HTMLElement | null = null;
  let targetHeadingValue: HTMLElement | null = null;
  let targetDistanceValue: HTMLElement | null = null;
  let batteryValue: HTMLElement | null = null;
  let engineValue: HTMLElement | null = null;
  let supplyValue: HTMLElement | null = null;
  let demandValue: HTMLElement | null = null;
  let fuelValue: HTMLElement | null = null;
  let reservesValue: HTMLElement | null = null;
  let loxValue: HTMLElement | null = null;
  let oxygenValue: HTMLElement | null = null;
  let heatValue: HTMLElement | null = null;
  let outsideValue: HTMLElement | null = null;
  let currentValue: HTMLElement | null = null;
  let integrityValue: HTMLElement | null = null;
  let breachValue: HTMLElement | null = null;

  // Crew tab
  let captainRoleAssignments: HTMLUListElement | null = null;
  let lastCrewSignature = '';
  let armedKickPlayerId: string | null = null;
  let armedKickTimeoutId: number | null = null;

  // Weapons tab
  let captainEmpButton: HTMLButtonElement | null = null;
  let captainEmpChargeButton: HTMLButtonElement | null = null;
  let captainFlareButton: HTMLButtonElement | null = null;
  let empArmed = false;
  let empArmTimeoutId: number | null = null;

  // Status tab
  let timerValue: HTMLElement | null = null;
  let distanceValue: HTMLElement | null = null;
  let captainSaveButton: HTMLButtonElement | null = null;
  let captainAudioAlertsButton: HTMLButtonElement | null = null;

  // Credits tab
  let creditsDisplay: HTMLElement | null = null;

  const setActiveTab = (tab: CaptainTabId): void => {
    activeTab = tab;
    for (const [id, button] of tabButtons) {
      button.classList.toggle('on', id === tab);
    }
    for (const [id, panel] of tabPanels) {
      panel.classList.toggle('active', id === tab);
    }
    if (tab === 'log') updateLogPanel(true);
  };

  const LOG_KIND_LABEL: Record<LogKind, string> = {
    transmission: 'Transmissions', wreck: 'Wreck logs', base: 'Base records', crew: 'Crew logs'
  };
  const LOG_KIND_ORDER: LogKind[] = ['transmission', 'wreck', 'base', 'crew'];

  /** Tab label with the unread count; pulses briefly when a new log arrives. */
  const updateLogTabButton = (): void => {
    const button = tabButtons.get('log');
    if (!button) return;
    const unread = countUnreadLogs(controllerState.lastRoomState);
    button.textContent = unread > 0 ? `Log (${unread})` : 'Log';
    button.classList.toggle('captain-log-unread', unread > 0);
  };

  const pulseLogTab = (): void => {
    const button = tabButtons.get('log');
    if (!button) return;
    button.classList.add('captain-log-pulse');
    if (logPulseTimer !== null) window.clearTimeout(logPulseTimer);
    logPulseTimer = window.setTimeout(() => { button.classList.remove('captain-log-pulse'); logPulseTimer = null; }, 6000);
  };

  const updateLogPanel = (force = false): void => {
    updateLogTabButton();
    if (!logPanelBody) return;
    const roomState = controllerState.lastRoomState;
    const campaign = roomState?.campaign;
    const recovered = getRecoveredLogs(roomState);
    const foundIds = getFoundLogIds(roomState);
    const storeKey = getLogStoreKey(roomState);
    // Logs are recovered but their text has not arrived (catalog fetch failed or is still running): keep asking (throttled inside).
    if (recovered.length < foundIds.length) void loadLogCatalog();
    const signature = [
      storeKey, foundIds.join(','), recovered.length, selectedLogId ?? '',
      recovered.map((l) => (isLogRead(storeKey, l.id) ? 1 : 0)).join('')
    ].join('|');
    if (!needsPanelRender({ force, signature, lastSignature: logSignature, target: logPanelBody, lastTarget: renderedLogBody })) return;
    logSignature = signature;
    renderedLogBody = logPanelBody;
    logPanelBody.replaceChildren();

    const total = foundIds.length;
    const summary = document.createElement('div');
    summary.className = 'captain-log-summary';
    summary.textContent = `${total} log${total === 1 ? '' : 's'} recovered`;
    logPanelBody.append(summary);
    if (recovered.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'captain-log-empty';
      empty.textContent = total > 0
        ? 'Loading log entries...'
        : 'No logs recovered yet. Transmissions, wreck records and base terminals are filed here as the crew finds them.';
      logPanelBody.append(empty);
      return;
    }

    const selected = recovered.find((l) => l.id === selectedLogId) ?? null;
    if (selected) {
      logPanelBody.append(buildLogReader(selected));
      return;
    }

    for (const chapter of campaign?.chapters ?? []) {
      const inChapter = recovered.filter((l) => l.chapter === chapter.id);
      if (inChapter.length === 0) continue;
      const heading = document.createElement('div');
      heading.className = 'captain-log-chapter';
      heading.textContent = chapter.title;
      logPanelBody.append(heading);
      for (const kind of LOG_KIND_ORDER) {
        const ofKind = inChapter.filter((l) => l.kind === kind);
        if (ofKind.length === 0) continue;
        const kindEl = document.createElement('div');
        kindEl.className = 'captain-log-kind';
        kindEl.textContent = LOG_KIND_LABEL[kind];
        logPanelBody.append(kindEl);
        for (const log of ofKind) logPanelBody.append(buildLogRow(log, storeKey));
      }
    }
    // Entries whose chapter is not in the manifest (or a map played without a campaign) still show.
    const known = new Set((campaign?.chapters ?? []).map((c) => c.id));
    const orphans = recovered.filter((l) => !known.has(l.chapter));
    if (orphans.length > 0) {
      if (campaign) {
        const heading = document.createElement('div');
        heading.className = 'captain-log-chapter';
        heading.textContent = 'Other';
        logPanelBody.append(heading);
      }
      for (const log of orphans) logPanelBody.append(buildLogRow(log, storeKey));
    }
  };

  const buildLogRow = (log: LogEntry, campaignId: string): HTMLButtonElement => {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'captain-log-row';
    const unread = !isLogRead(campaignId, log.id);
    row.classList.toggle('unread', unread);
    const title = document.createElement('span');
    title.className = 'captain-log-row-title';
    title.textContent = log.title;
    row.append(title);
    if (unread) {
      const badge = document.createElement('span');
      badge.className = 'captain-log-badge';
      badge.textContent = 'NEW';
      row.append(badge);
    }
    if (log.audio) {
      const voiced = document.createElement('span');
      voiced.className = 'captain-log-voiced';
      voiced.textContent = 'VOICE';
      row.append(voiced);
    }
    row.addEventListener('click', () => {
      selectedLogId = log.id;
      markLogRead(campaignId, log.id); // notifies onLogsChanged -> re-renders the reader
      updateLogPanel(true);
    });
    return row;
  };

  const buildLogReader = (log: LogEntry): HTMLElement => {
    const wrap = document.createElement('div');
    wrap.className = 'captain-log-reader';
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'captain-log-back';
    back.textContent = '< All logs';
    back.addEventListener('click', () => { selectedLogId = null; updateLogPanel(true); });
    const title = document.createElement('h3');
    title.className = 'captain-log-title';
    title.textContent = log.title;
    const meta = document.createElement('div');
    meta.className = 'captain-log-meta';
    meta.textContent = `${LOG_KIND_LABEL[log.kind] ?? log.kind} - ${log.speaker}`;
    const body = document.createElement('div');
    body.className = 'captain-log-body';
    body.textContent = log.body;
    const replay = document.createElement('button');
    replay.type = 'button';
    replay.className = 'captain-log-replay';
    replay.textContent = log.audio ? 'Replay transmission' : 'Show on TV again';
    replay.addEventListener('click', () => replayLog(log));
    wrap.append(back, title, meta, body, replay);
    return wrap;
  };

  /** A log unlocked: pulse the tab and refresh (the controller page forwards EVENTS.logUnlocked here). */
  const notifyLogUnlocked = (log: LogEntry): void => {
    // The event can beat the room state that lists the id: record it so the Log tab shows the entry straight away.
    const found = controllerState.lastRoomState?.campaign?.logsFound ?? controllerState.lastRoomState?.missionScript?.logsFound;
    if (found && !found.includes(log.id)) found.push(log.id);
    addLogToCatalog(log);
    pulseLogTab();
    updateLogPanel();
  };

  onLogsChanged(() => updateLogPanel());

  const disarmEmp = (): void => {
    if (empArmTimeoutId !== null) {
      window.clearTimeout(empArmTimeoutId);
      empArmTimeoutId = null;
    }
    empArmed = false;
  };

  const disarmKick = (): void => {
    if (armedKickTimeoutId !== null) {
      window.clearTimeout(armedKickTimeoutId);
      armedKickTimeoutId = null;
    }
    armedKickPlayerId = null;
  };

  const expandedGroups = new Set<string>();
  const autoOpenedGroups = new Set<string>();
  /** The mission list element the last render filled (see needsPanelRender). */
  let renderedMissionList: HTMLUListElement | null = null;
  /** Mission rows the crew has expanded. The active mission opens by itself the first time it becomes active. */
  const expandedMissions = new Set<string>();
  const autoOpenedMissions = new Set<string>();
  let missionSignature = '';

  /** "Reward: 2 × Carbon, 1 × Fuel" line for a mission, or null when it pays nothing. */
  const rewardLine = (mission: { reward?: Array<{ id: string; quantity: number }> }, done: boolean): HTMLElement | null => {
    const items = mission.reward ?? [];
    if (items.length === 0) return null;
    const el = document.createElement('div');
    el.style.cssText = 'font-size:0.68rem;margin-top:0.2rem;color:#e8b84b;';
    el.textContent = `${done ? 'Rewarded' : 'Reward'}: ${items.map((r) => `${r.quantity.toLocaleString()} × ${itemDisplayName(r.id)}`).join(', ')}`;
    return el;
  };

  /** One tappable mission row: title (toggles the detail) and, when open, the description, checklist and waypoint button. */
  const buildMissionRow = (m: MissionObjective, state: MissionVisualState, navWaypoint: GameSnapshot['navWaypoint'] | undefined, indented: boolean): HTMLLIElement => {
    const li = document.createElement('li');
    li.className = `captain-mission-row ${state}`;
    li.style.listStyle = 'none';
    li.style.opacity = state === 'active' ? '1' : state === 'done' ? '0.4' : '0.55';
    if (indented) li.style.paddingLeft = '1.2rem';
    const open = expandedMissions.has(m.id);
    const head = document.createElement('div');
    head.className = 'captain-mission-head';
    head.setAttribute('role', 'button');
    head.tabIndex = 0;
    head.setAttribute('aria-expanded', String(open));
    head.style.fontWeight = state === 'active' ? '700' : '400';
    head.style.cursor = 'pointer';
    head.textContent = `${state === 'done' ? '✓' : state === 'active' ? '◉' : '○'} ${open ? '▾' : '▸'} ${m.title}`;
    const toggle = (): void => {
      if (expandedMissions.has(m.id)) expandedMissions.delete(m.id);
      else expandedMissions.add(m.id);
      updateMissionPanel(true);
    };
    head.addEventListener('click', toggle);
    head.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggle(); }
    });
    li.append(head);
    // Dev only: the server says so in RoomState.devTools (non-production) and validates the action again.
    if (controllerState.lastRoomState?.devTools && state !== 'done' && !m.optional) {
      li.append(buildSkipMissionButton(m.id, (input) => emitControl(input)));
    }
    if (open) {
      li.append(buildMissionDetail({
        objective: m,
        navWaypoint,
        setNavWaypoint: (waypoint) => getControllerNetwork()?.setNavWaypoint(waypoint)
      }));
    }
    const reward = rewardLine(m, state === 'done');
    if (reward) li.append(reward);
    return li;
  };

  /** The overarching mission: collapsible row (title, summary, "Step 2 of 4", reward) with its sub-missions as a checklist beneath. */
  const buildGroupBlock = (view: MissionGroupView, missions: MissionObjective[], states: MissionVisualState[], navWaypoint: GameSnapshot['navWaypoint'] | undefined): HTMLLIElement => {
    const li = document.createElement('li');
    li.className = `captain-mission-group ${view.state}`;
    li.style.listStyle = 'none';
    const expanded = expandedGroups.has(view.groupId);
    const head = document.createElement('div');
    head.className = 'captain-mission-group-head';
    head.setAttribute('role', 'button');
    head.tabIndex = 0;
    head.setAttribute('aria-expanded', String(expanded));
    head.style.cssText = `display:flex;align-items:baseline;gap:0.4rem;cursor:pointer;font-weight:${view.state === 'active' ? '700' : '400'};opacity:${view.state === 'active' ? '1' : view.state === 'done' ? '0.45' : '0.6'};`;
    const title = document.createElement('span');
    title.className = 'captain-mission-group-title';
    title.style.flex = '1';
    title.textContent = `${view.state === 'done' ? '✓' : view.state === 'active' ? '◉' : '○'} ${expanded ? '▾' : '▸'} ${view.title}`;
    head.append(title);
    const progress = groupProgressLabel(view);
    if (progress) {
      const chip = document.createElement('span');
      chip.className = 'captain-mission-group-progress';
      chip.style.cssText = 'font-size:0.68rem;font-weight:400;color:#7fe3ff;white-space:nowrap;';
      chip.textContent = progress;
      head.append(chip);
    }
    const toggle = (): void => {
      if (expandedGroups.has(view.groupId)) expandedGroups.delete(view.groupId); else expandedGroups.add(view.groupId);
      updateMissionPanel(true);
    };
    head.addEventListener('click', toggle);
    head.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggle(); } });
    li.append(head);
    if (view.summary) {
      const summary = document.createElement('div');
      summary.className = 'captain-mission-group-summary';
      summary.style.cssText = 'font-size:0.72rem;opacity:0.75;margin-top:0.1rem;';
      summary.textContent = view.summary;
      li.append(summary);
    }
    const reward = rewardLine({ reward: view.reward }, view.state === 'done');
    if (reward) li.append(reward);
    if (expanded) {
      const list = document.createElement('ul');
      list.className = 'captain-mission-substeps';
      list.style.cssText = 'margin:0.3rem 0 0;padding:0;';
      for (const index of view.indices) list.append(buildMissionRow(missions[index], states[index], navWaypoint, true));
      li.append(list);
    }
    return li;
  };

  const updateMissionPanel = (force = false, snapshot: GameSnapshot | null = controllerState.lastSnapshot): void => {
    const roomState = controllerState.lastRoomState;
    const missions = roomState?.missionObjectives ?? [];
    const activeIndex = roomState?.activeMissionIndex ?? 0;
    const timerMs = roomState?.activeMissionTimerMs ?? null;
    const navWaypoint = snapshot?.navWaypoint ?? undefined;
    const states = missionStates(missions, activeIndex);

    states.forEach((state, i) => {
      if (state === 'active' && !autoOpenedMissions.has(missions[i].id)) {
        autoOpenedMissions.add(missions[i].id);
        expandedMissions.add(missions[i].id);
      }
    });

    const views = buildGroupViews(missions, states);
    // The overarching mission the crew is on opens by itself the first time it becomes active (they can still collapse it).
    for (const view of views) {
      if (view.state === 'active' && isGroupRow(view) && !autoOpenedGroups.has(view.groupId)) {
        autoOpenedGroups.add(view.groupId);
        expandedGroups.add(view.groupId);
      }
    }

    const signature = JSON.stringify([
      missions, states, roomState?.devTools === true, [...expandedMissions], [...expandedGroups], missions.map((m) => (isWaypointAtMission(m, navWaypoint) ? 1 : 0))
    ]);

    if (captainMissionList && needsPanelRender({ force, signature, lastSignature: missionSignature, target: captainMissionList, lastTarget: renderedMissionList })) {
      missionSignature = signature;
      renderedMissionList = captainMissionList;
      captainMissionList.innerHTML = '';

      if (views.length === 0) {
        const li = document.createElement('li');
        li.textContent = t('no_missions');
        li.style.opacity = '0.5';
        captainMissionList.append(li);
      }

      for (const view of views) {
        if (!isGroupRow(view)) {
          const idx = view.indices[0];
          captainMissionList.append(buildMissionRow(missions[idx], states[idx], navWaypoint, false));
          continue;
        }
        captainMissionList.append(buildGroupBlock(view, missions, states, navWaypoint));
      }
    }

    if (captainMissionTimerEl) {
      if (typeof timerMs === 'number' && timerMs > 0) {
        const totalSec = Math.ceil(timerMs / 1000);
        const mins = Math.floor(totalSec / 60);
        const secs = totalSec % 60;
        captainMissionTimerEl.textContent = `⏱ ${mins}:${String(secs).padStart(2, '0')}`;
        captainMissionTimerEl.style.color = totalSec <= 60 ? '#ff8b8b' : '';
        captainMissionTimerEl.hidden = false;
      } else {
        captainMissionTimerEl.hidden = true;
      }
    }
  };

  loadItemNames(() => updateMissionPanel(true));

  const buildCrewRows = (): void => {
    if (!captainRoleAssignments) {
      return;
    }

    disarmKick();
    captainRoleAssignments.innerHTML = '';
    const players = controllerState.lastRoomState?.players ?? [];
    const sortedPlayers = [...players].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    for (const player of sortedPlayers) {
      const row = document.createElement('li');
      row.className = 'captain-role-assignment-row';

      const playerName = document.createElement('span');
      playerName.className = 'captain-role-assignment-label';
      playerName.textContent = player.name || 'Unknown crew member';

      const assignedRoom = document.createElement('strong');
      assignedRoom.className = 'captain-role-assignment-value';
      assignedRoom.textContent = player.role ? resolveRoleMeta(player.role).label : 'No room';

      row.append(playerName, assignedRoom);

      // No kick button for the captain's own row.
      if (player.id && player.id !== controllerState.currentPlayerId) {
        const kickButton = document.createElement('button');
        kickButton.type = 'button';
        kickButton.className = 'captain-kick-btn';
        kickButton.textContent = t('kick');
        kickButton.addEventListener('click', () => {
          const code = controllerState.currentRoomCode;
          if (!code) {
            return;
          }

          if (armedKickPlayerId === player.id) {
            disarmKick();
            getControllerNetwork()?.kickPlayer({ code, playerId: player.id });
            return;
          }

          // Arm this button and disarm any other.
          disarmKick();
          armedKickPlayerId = player.id;
          captainRoleAssignments?.querySelectorAll('.captain-kick-btn.armed').forEach((el) => {
            el.classList.remove('armed');
            (el as HTMLButtonElement).textContent = 'Kick';
          });
          kickButton.classList.add('armed');
          kickButton.textContent = t('confirm');
          armedKickTimeoutId = window.setTimeout(() => {
            armedKickPlayerId = null;
            armedKickTimeoutId = null;
            kickButton.classList.remove('armed');
            kickButton.textContent = t('kick');
          }, KICK_CONFIRM_TIMEOUT_MS);
        });
        row.append(kickButton);
      }

      captainRoleAssignments.append(row);
    }

    if (sortedPlayers.length === 0) {
      const li = document.createElement('li');
      li.className = 'captain-role-assignment-row';
      li.textContent = t('no_crew');
      li.style.opacity = '0.55';
      captainRoleAssignments.append(li);
    }
  };

  const updateCrewPanel = (): void => {
    const players = controllerState.lastRoomState?.players ?? [];
    // Only rebuild when the roster actually changes, so an armed kick button isn't destroyed mid-tick.
    const signature = players
      .map((p) => `${p.id}:${p.name}:${p.role ?? ''}`)
      .sort()
      .join('|');
    if (signature === lastCrewSignature) {
      return;
    }
    lastCrewSignature = signature;
    buildCrewRows();
  };

  const updateSubmarinePanel = (snapshot: GameSnapshot | null): void => {
    if (!snapshot) {
      return;
    }
    const sub = snapshot.submarine;
    const roomState = controllerState.lastRoomState;

    if (depthValue) {
      setSeg(depthValue, `${Math.round(sub.depth)}`, 'amber');
    }
    if (speedValue) {
      const speed = Math.hypot(sub.speed, sub.lateralSpeed, sub.verticalVelocity);
      setSeg(speedValue, speed.toFixed(1), 'cyan');
    }
    if (vRateValue) {
      const rate = sub.verticalVelocity;
      const sign = rate > 0.005 ? '+' : '';
      setSeg(vRateValue, `${sign}${rate.toFixed(2)}`, Math.abs(rate) >= 1 ? 'red' : 'cyan');
    }
    if (supplyValue) {
      setSeg(supplyValue, Number.isFinite(sub.powerSupplyMw) ? `${Math.round(sub.powerSupplyMw)}` : '--', 'cyan');
    }
    if (demandValue) {
      // Highlight when continuous draw outstrips supply (battery is discharging).
      const overdraw = sub.powerDemandMw > sub.powerSupplyMw + 0.5;
      setSeg(demandValue, Number.isFinite(sub.powerDemandMw) ? `${Math.round(sub.powerDemandMw)}` : '--', overdraw ? 'red' : 'amber');
    }
    if (loxValue) {
      setSeg(loxValue, Number.isFinite(sub.liquidOxygen) ? `${Math.round(sub.liquidOxygen)}` : '--', sub.loxBoostActive ? 'red' : 'cyan');
    }
    if (currentValue) {
      const flow = sub.currentFlow;
      const speed = flow ? Math.hypot(flow.x, flow.z) : 0;
      // Compass-style bearing the water is flowing TOWARD: 0° = north (-Z), 90° = east (+X).
      const bearing = flow && speed > 0.01 ? (Math.atan2(flow.x, -flow.z) * 180 / Math.PI + 360) % 360 : null;
      setSeg(currentValue, bearing === null ? '0.0' : `${speed.toFixed(1)}@${Math.round(bearing)}`, 'cyan');
    }
    if (outsideValue) {
      setSeg(outsideValue, `${Math.round(sub.outsideTemperature)}`, 'cyan');
    }
    if (breachValue) {
      const breaches = Object.values(sub.hullBreaches).filter(Boolean).length;
      const flooding = Object.values(sub.roomFlood ?? {}).some((level) => (level ?? 0) > 0);
      setSeg(breachValue, `${breaches}`, breaches > 0 || flooding ? 'red' : 'amber');
    }

    if (fuelValue) {
      const fuelType = FUEL_PROFILE_BY_TYPE[sub.currentFuelType]?.label ?? sub.currentFuelType;
      setSeg(fuelValue, `${Math.round(sub.fuel)}/${Math.round(sub.fuelCapacity)}`, resourceReadoutVariant('fuel', sub.fuel));
      if (fuelValue.parentElement) {
        fuelValue.parentElement.title = `Current tank: ${fuelType}`;
      }
    }
    if (reservesValue) {
      const reserveCans = FUEL_TYPES.reduce(
        (sum, type) => sum + (roomState?.cargo?.[FUEL_PRODUCT_ID_BY_TYPE[type]] ?? 0),
        0
      );
      setSeg(reservesValue, `${reserveCans}`, 'amber');
    }
    if (batteryValue) {
      setSeg(batteryValue, `${Math.round(sub.batteryCharge)}`, resourceReadoutVariant('battery', sub.batteryCharge, 'cyan'));
    }
    if (engineValue) {
      setSeg(engineValue, `${Math.round(sub.engineOutput)}`, sub.engineOverloaded ? 'red' : 'amber');
    }
    if (heatValue) {
      setSeg(heatValue, `${Math.round(sub.insideTemperature)}`, sub.insideTemperature >= 45 ? 'red' : 'amber');
    }
    if (oxygenValue) {
      setSeg(oxygenValue, `${Math.round(sub.oxygen)}`, resourceReadoutVariant('oxygen', sub.oxygen, 'cyan'));
    }
    if (integrityValue) {
      setSeg(integrityValue, `${Math.round(sub.hull)}`, resourceReadoutVariant('hull', sub.hull));
    }
    if (headingValue) {
      const heading = normalizeHeadingDegrees(sub.yaw);
      setSeg(headingValue, `${Math.round(heading)} ${headingDegreesToCardinal(heading)}`, 'cyan');
    }

    const waypoint = snapshot.navWaypoint ?? null;
    if (waypoint) {
      const subPoint = submarineToMapPoint(sub);
      const wpPoint = simulationToMapPoint({ x: waypoint.simulationX, y: waypoint.simulationZ });
      const bearing = getBearingToMapPoint(subPoint, wpPoint);
      // Map metres (simulation units are not metres: x x10, z x4.2), and 3D when the waypoint has a depth: a mission point
      // straight below the boat is a long way off even though it is "at" the boat on the plan view.
      const distanceMeters = Math.hypot(
        wpPoint.x - subPoint.x,
        wpPoint.z - subPoint.z,
        typeof waypoint.depth === 'number' ? waypoint.depth - sub.depth : 0
      );
      if (targetHeadingValue) {
        setSeg(targetHeadingValue, `${Math.round(bearing)} ${headingDegreesToCardinal(bearing)}`, 'cyan');
      }
      if (targetDistanceValue) {
        setSeg(targetDistanceValue, `${Math.round(distanceMeters)}`, 'amber');
      }
    } else {
      if (targetHeadingValue) {
        setSeg(targetHeadingValue, 'NO WPT', 'amber');
      }
      if (targetDistanceValue) {
        setSeg(targetDistanceValue, '--', 'amber');
      }
    }
  };

  const updateCreditsPanel = (): void => {
    if (creditsDisplay) {
      const credits = controllerState.lastRoomState?.credits ?? 0;
      setSeg(creditsDisplay, credits.toLocaleString(), 'cyan');
    }
  };

  const updateStatusPanel = (snapshot: GameSnapshot | null): void => {
    if (!snapshot) {
      return;
    }
    if (timerValue) {
      setSeg(timerValue, formatClock(snapshot.tick * SIMULATION_INTERVAL_MS), 'cyan');
    }
    if (distanceValue) {
      setSeg(distanceValue, formatDistance(snapshot.distanceTravelledMeters ?? 0), 'amber');
    }
  };

  const updatePanel = (snapshot: GameSnapshot | null): void => {
    updateMissionPanel(false, snapshot ?? controllerState.lastSnapshot);
    updateLogPanel();
    updateCrewPanel();
    updateSubmarinePanel(snapshot);
    updateStatusPanel(snapshot);
    updateCreditsPanel();
  };

  // Retained for compatibility with the controller dispatch which calls it on roster changes.
  const updateRoleAssignments = (): void => {
    updateCrewPanel();
  };

  const updateActionButtons = (snapshot: GameSnapshot | null): void => {
    if (!snapshot) {
      return;
    }

    if (captainEmpButton) {
      const locked = snapshot.submarine.empLocked || snapshot.submarine.powerFailure;
      captainEmpButton.disabled = locked;
      if (locked) {
        disarmEmp();
      }
      captainEmpButton.classList.toggle('armed', empArmed);
      captainEmpButton.textContent = snapshot.submarine.empLocked
        ? 'EMP RECHARGING'
        : empArmed
          ? 'TAP AGAIN TO FIRE'
          : 'FIRE EMP';
      captainEmpButton.style.opacity = locked ? '0.45' : '1';
    }

    const stock = controllerState.lastRoomState?.cargo ?? {};
    if (captainEmpChargeButton) {
      const count = Math.floor(stock['emp-charge'] ?? 0);
      captainEmpChargeButton.textContent = `EMP CHARGE (${count})`;
      captainEmpChargeButton.disabled = count < 1 || snapshot.submarine.empLocked || snapshot.submarine.powerFailure;
      captainEmpChargeButton.style.opacity = captainEmpChargeButton.disabled ? '0.45' : '1';
    }
    if (captainFlareButton) {
      const count = Math.floor(stock['flare'] ?? 0);
      captainFlareButton.textContent = `LAUNCH FLARE (${count})`;
      captainFlareButton.disabled = count < 1 || snapshot.submarine.docked;
      captainFlareButton.style.opacity = captainFlareButton.disabled ? '0.45' : '1';
    }

    if (captainAudioAlertsButton) {
      const enabled = snapshot.submarine.audioAlertsEnabled !== false;
      captainAudioAlertsButton.textContent = enabled ? 'AUDIO ALERTS: ON' : 'AUDIO ALERTS: OFF';
      captainAudioAlertsButton.classList.toggle('armed', !enabled);
    }

    if (captainSaveButton) {
      const isDocked = snapshot.submarine.docked;
      captainSaveButton.disabled = !isDocked;
      captainSaveButton.title = isDocked
        ? 'Save the current game while docked.'
        : 'Save is only available while the submarine is docked.';
    }
  };

  const buildTabBar = (): void => {
    if (!ctrlZoneLeftBottom) {
      return;
    }
    const bar = document.createElement('div');
    bar.className = 'captain-tab-bar';
    for (const tab of CAPTAIN_TABS) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'captain-tab-btn';
      button.textContent = tab.label;
      button.addEventListener('click', () => setActiveTab(tab.id));
      tabButtons.set(tab.id, button);
      bar.append(button);
    }
    ctrlZoneLeftBottom.append(bar);
  };

  const buildMissionPanel = (): HTMLDivElement => {
    const panel = document.createElement('div');
    panel.className = 'captain-tab-panel';

    const card = createCard('Mission overview', 'Track mission progress and coordinate the crew.');
    card.classList.add('ctrl-room-main-card');

    const timerEl = document.createElement('div');
    timerEl.style.cssText = 'font-family:monospace;font-size:0.85rem;font-weight:700;color:#ff8b8b;margin-bottom:0.4rem;';
    timerEl.hidden = true;
    captainMissionTimerEl = timerEl;
    card.append(timerEl);

    const checklist = document.createElement('ul');
    checklist.className = 'captain-checklist';
    captainMissionList = checklist;
    missionSignature = ''; // a freshly built list is empty: the next update must fill it
    card.append(checklist);

    panel.append(card);
    return panel;
  };

  const buildLogPanel = (): HTMLDivElement => {
    const panel = document.createElement('div');
    panel.className = 'captain-tab-panel';
    const card = createCard("Captain's log", 'Transmissions, wreck records and crew logs recovered so far.');
    card.classList.add('ctrl-room-main-card');
    const body = document.createElement('div');
    body.className = 'captain-log-list';
    logPanelBody = body;
    logSignature = '';
    renderedLogBody = null;
    card.append(body);
    panel.append(card);
    void loadLogCatalog();
    return panel;
  };

  const buildSubmarinePanel = (): HTMLDivElement => {
    const panel = document.createElement('div');
    panel.className = 'captain-tab-panel';

    const card = createCard('Submarine overview', 'Key telemetry for the whole boat.');
    card.classList.add('ctrl-room-main-card');

    const grid = document.createElement('div');
    grid.className = 'captain-readout-grid';

    // Readouts flow left-to-right, top-to-bottom across two columns, paired by theme:
    // motion, navigation, power, fuel, life-support, hull. Units render as a sub-label
    // beneath the readout name (empty string = no unit).
    const specs: Array<[string, string, ReadoutVariant, (el: HTMLElement) => void]> = [
      ['Depth', 'metres', 'amber', (el) => (depthValue = el)],
      ['Speed', 'metres/sec', 'cyan', (el) => (speedValue = el)],
      ['V-rate', 'metres/sec', 'cyan', (el) => (vRateValue = el)],
      ['Heading', 'degrees', 'cyan', (el) => (headingValue = el)],
      ['Target hdg', 'degrees', 'cyan', (el) => (targetHeadingValue = el)],
      ['Target range', 'metres', 'amber', (el) => (targetDistanceValue = el)],
      ['Battery', 'percent', 'cyan', (el) => (batteryValue = el)],
      ['Engine out', 'megawatt', 'amber', (el) => (engineValue = el)],
      ['Power in', 'megawatt', 'cyan', (el) => (supplyValue = el)],
      ['Power draw', 'megawatt', 'amber', (el) => (demandValue = el)],
      ['Fuel (tank)', 'units', 'amber', (el) => (fuelValue = el)],
      ['Fuel reserve', 'canisters', 'amber', (el) => (reservesValue = el)],
      ['Liquid O₂', 'percent', 'cyan', (el) => (loxValue = el)],
      ['Oxygen', 'percent', 'cyan', (el) => (oxygenValue = el)],
      ['Cabin temp', 'celsius', 'amber', (el) => (heatValue = el)],
      ['Water temp', 'celsius', 'cyan', (el) => (outsideValue = el)],
      ['Current', 'm/s @ bearing', 'cyan', (el) => (currentValue = el)],
      ['Hull', 'percent', 'amber', (el) => (integrityValue = el)],
      ['Breaches', 'zones', 'amber', (el) => (breachValue = el)]
    ];
    for (const [label, unit, variant, assign] of specs) {
      const r = createReadoutRow(label, '--', variant, unit);
      assign(r.valueElement);
      grid.append(r.row);
    }
    card.append(grid);

    panel.append(card);
    return panel;
  };

  const buildCrewPanel = (): HTMLDivElement => {
    const panel = document.createElement('div');
    panel.className = 'captain-tab-panel';

    const card = createCard('Crew manifest', 'See connected crew and remove disruptive members.');
    card.classList.add('ctrl-room-main-card');

    captainRoleAssignments = document.createElement('ul');
    captainRoleAssignments.className = 'captain-role-assignments';
    card.append(captainRoleAssignments);

    panel.append(card);
    return panel;
  };

  const buildWeaponsPanel = (): HTMLDivElement => {
    const panel = document.createElement('div');
    panel.className = 'captain-tab-panel';

    const card = createCard('Weapons', 'Emergency electromagnetic pulse.');
    card.classList.add('ctrl-room-bottom-card');

    const description = document.createElement('p');
    description.className = 'captain-emp-description';
    description.textContent =
      'EMERGENCY USE ONLY. Firing the EMP instantly discharges the entire battery to stun, damage and shake off every hostile within 45 m (drones are shielded). All thrusters, the engine, and exterior lights shut off on discharge. The EMP then stays locked until the battery recharges to 50%.';
    card.append(description);

    const empButton = document.createElement('button');
    empButton.type = 'button';
    empButton.className = 'captain-emp-btn';
    empButton.textContent = t('fire_emp');
    captainEmpButton = empButton;
    empButton.addEventListener('click', () => {
      if (!controllerState.currentRoomCode || !controllerState.lastSnapshot) {
        return;
      }
      // The EMP drains the entire battery, so it needs a deliberate second tap.
      if (!empArmed) {
        empArmed = true;
        empArmTimeoutId = window.setTimeout(() => {
          empArmed = false;
          empArmTimeoutId = null;
          updateActionButtons(controllerState.lastSnapshot);
        }, KICK_CONFIRM_TIMEOUT_MS);
        updateActionButtons(controllerState.lastSnapshot);
        return;
      }
      disarmEmp();
      emitControl({ action: 'trigger_emp' });
      updateActionButtons(controllerState.lastSnapshot);
    });
    card.append(empButton);

    // Consumables from storage: a cheaper, smaller EMP and a decoy flare that draws creatures away.
    const consumableRow = document.createElement('div');
    consumableRow.style.cssText = 'display:flex;gap:0.5rem;margin-top:0.5rem;';
    const chargeButton = document.createElement('button');
    chargeButton.type = 'button';
    chargeButton.className = 'captain-emp-btn';
    chargeButton.style.flex = '1';
    chargeButton.textContent = 'EMP CHARGE';
    chargeButton.addEventListener('click', () => {
      if (controllerState.currentRoomCode && controllerState.lastSnapshot) emitControl({ action: 'trigger_emp_charge' });
    });
    const flareButton = document.createElement('button');
    flareButton.type = 'button';
    flareButton.className = 'captain-emp-btn';
    flareButton.style.flex = '1';
    flareButton.textContent = 'LAUNCH FLARE';
    flareButton.addEventListener('click', () => {
      if (controllerState.currentRoomCode && controllerState.lastSnapshot) emitControl({ action: 'launch_flare' });
    });
    captainEmpChargeButton = chargeButton;
    captainFlareButton = flareButton;
    consumableRow.append(chargeButton, flareButton);
    card.append(consumableRow);

    panel.append(card);
    return panel;
  };

  const buildStatusPanel = (): HTMLDivElement => {
    const panel = document.createElement('div');
    panel.className = 'captain-tab-panel';

    const card = createCard('Game status', 'Run statistics and saving.');
    card.classList.add('ctrl-room-main-card');

    const timer = createReadoutRow('Run time', '--', 'cyan');
    timerValue = timer.valueElement;
    const distance = createReadoutRow('Distance travelled', '--', 'amber');
    distanceValue = distance.valueElement;
    card.append(timer.row, distance.row);

    const audioButton = document.createElement('button');
    audioButton.type = 'button';
    audioButton.className = 'captain-save-btn';
    audioButton.textContent = 'AUDIO ALERTS: ON';
    audioButton.title = 'Silence or restore the proximity warning and alarm beeps on the cast screens.';
    captainAudioAlertsButton = audioButton;
    audioButton.addEventListener('click', () => {
      emitControl({ action: 'toggle_audio_alerts' });
    });
    card.append(audioButton);

    const saveButton = document.createElement('button');
    saveButton.type = 'button';
    saveButton.className = 'primary captain-save-btn';
    saveButton.textContent = t('save_game');
    saveButton.disabled = true;
    saveButton.title = 'Save is only available while the submarine is docked.';
    captainSaveButton = saveButton;
    saveButton.addEventListener('click', () => {
      if (saveButton.disabled) {
        return;
      }
      requestSaveGame();
    });
    card.append(saveButton);

    panel.append(card);
    return panel;
  };

  const buildCreditsPanel = (): HTMLDivElement => {
    const panel = document.createElement('div');
    panel.className = 'captain-tab-panel';

    const card = createCard('Ship credits', 'Shared currency earned from missions and salvage.');
    card.classList.add('ctrl-room-main-card');

    const row = createReadoutRow('Balance', '--', 'cyan', 'CR');
    creditsDisplay = row.valueElement;
    card.append(row.row);

    panel.append(card);
    return panel;
  };

  const render = (): void => {
    if (!controls) {
      return;
    }
    controls.classList.add('captain-role-layout');

    tabButtons.clear();
    tabPanels.clear();
    lastCrewSignature = '';
    disarmKick();

    buildTabBar();

    tabPanels.set('mission', buildMissionPanel());
    tabPanels.set('log', buildLogPanel());
    tabPanels.set('submarine', buildSubmarinePanel());
    tabPanels.set('crew', buildCrewPanel());
    tabPanels.set('weapons', buildWeaponsPanel());
    tabPanels.set('status', buildStatusPanel());
    tabPanels.set('credits', buildCreditsPanel());

    for (const tab of CAPTAIN_TABS) {
      const panel = tabPanels.get(tab.id);
      if (panel) {
        controls.append(panel);
      }
    }

    setActiveTab(activeTab);

    updatePanel(controllerState.lastSnapshot);
    updateActionButtons(controllerState.lastSnapshot);
  };

  return {
    notifyLogUnlocked,
    render,
    updatePanel,
    updateRoleAssignments,
    updateActionButtons
  };
};
